<script>
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import IconCamera from "@js/components/Icons/IconCamera.vue";
import IconClip from "@js/components/Icons/IconClip.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import {ref} from "vue";
import {useSearchStore} from "@js/store/SearchStore.js";
import IconCameraYellowGradient from "@js/components/Icons/IconCameraYellowGradient.vue";

export default {
    name: "ImageSearchUploadSection",
    components: {
        IconCameraYellowGradient,
        SpinnerIndicator,
        OnlyMobile,
        IconClip,
        IconCamera,
        OnlyDesktop
    },
    props: {
        theme: {
            required: false,
            type: String,
            default: "light"
        }
    },
    setup() {
        const searchStore = useSearchStore();

        const fileInputRef = ref(null);
        const isDragOver = ref(false);
        // Обработчики drag’n’drop
        const onDragOver = (e) => {
            e.preventDefault();
            isDragOver.value = true;
        };
        const onDragLeave = (e) => {
            e.preventDefault();
            isDragOver.value = false;
        };
        const onDrop = (e) => {
            isDragOver.value = false;
            const files = e.dataTransfer.files;
            if (files.length) {
                handleFiles(files);
            }
        };

        // Вызывается и при выборе через input, и при drop
        const handleFiles = (files) => {
            const file = files[0];

            if (searchStore.imageSearch.previewUrl) {
                URL.revokeObjectURL(searchStore.imageSearch.previewUrl)
            }
            searchStore.imageSearch.previewUrl = URL.createObjectURL(file)

            // здесь вызываем ваш экшен: например, searchStore.searchByImage(file)
            searchStore.searchByImage(file);
        };

        // onChange у input
        const onFileChange = (e) => {
            const files = e.target.files;
            if (files.length) {
                handleFiles(files);
                // очищаем input, если хотим доп. загрузку того же файла
                e.target.value = null;
            }
        };

        return {
            searchStore,
            isDragOver,
            onDragOver,
            onDragLeave,
            onDrop,
            onFileChange,
        }
    }
}
</script>

<template>
    <div class="flex items-center justify-center w-full h-full"
         :class="[theme === 'light' ? 'theme-light' : 'theme-dark']">
        <template v-if="!searchStore.imageSearch.imageSearchInProgress">
            <OnlyDesktop :maxSize="1024"
                         class="flex flex-col items-center justify-center w-full h-full dashed-bg rounded-3xl bg-transparent transition relative"
                         :class="{ 'drag-over': isDragOver, 'text-white': theme === 'light', 'text-black': theme === 'dark' }"
                         @dragover.prevent="onDragOver"
                         @dragenter.prevent="onDragOver"
                         @dragleave.prevent="onDragLeave"
                         @drop.prevent="onDrop">
                <div class="flex flex-col items-center justify-center pt-5 pb-6 mb-5">
                    <IconCamera class="w-16 h-16"
                                :class="[theme === 'light' ? 'text-baseColor' : 'text-black']"/>
                    <p class="mb-2 text-3xl font-semibold">Найдите товары по фото</p>
                    <p class="font-medium">Загрузите фото товара или интерьера</p>
                </div>
                <label
                    class="inline-flex items-center justify-center gap-2 leading-tight px-8 py-4 bg-baseColor md:hover:bg-baseColor/90 rounded-lg cursor-pointer transition text-black font-medium mb-5">
                    <input type="file" accept="image/*" class="hidden"
                           autocomplete="off"
                           @change="onFileChange"/>
                    <IconClip class="w-5 h-5 rotate-45"/>
                    Загрузить фото
                </label>
                <p class="font-medium text-sm">
                    или перетащите его в пунктирное поле
                </p>
            </OnlyDesktop>
            <OnlyMobile :maxSize="1024">
                <div
                    class="w-full flex flex-col items-center justify-between pt-5 pb-6 text-black">
                    <div class="flex flex-col items-center mb-10">
                        <IconCamera class="w-16 h-16 text-gray-800"/>
                        <p class="mb-2 text-2xl font-semibold">Найдите товары по фото</p>
                        <p class="font-semibold">Загрузите фото товара или интерьера</p>
                    </div>
                    <div class="flex flex-col items-center justify-center w-full space-y-4">
                        <label
                            class="w-full inline-flex items-center justify-center gap-2 leading-tight px-4 py-4 bg-baseColor rounded-lg cursor-pointer transition text-black font-medium">
                            <input type="file"
                                   accept="image/*"
                                   capture="environment"
                                   class="hidden"
                                   @change="onFileChange"/>
                            <IconCamera class="w-5 h-5"/>
                            Сфотографировать
                        </label>
                        <label
                            class="w-full inline-flex items-center justify-center gap-2 leading-tight px-4 py-4 bg-white border border-gray-400 rounded-lg cursor-pointer md:hover:bg-opacity-30 transition text-black font-medium">
                            <input type="file" accept="image/*" class="hidden" @change="onFileChange"/>
                            <IconClip class="w-5 h-5 rotate-45"/>
                            Загрузить фото
                        </label>
                    </div>
                </div>
            </OnlyMobile>
        </template>
        <template v-else>
            <SpinnerIndicator class="w-16 h-16"/>
        </template>
    </div>
</template>

<style scoped lang="scss">
.theme-light .dashed-bg {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23fff' stroke-dasharray='8, 10' stroke-dashoffset='7' stroke-linecap='square'/%3E%3C/svg%3E");
    transition: background-color 0.2s ease;
}

.theme-dark .dashed-bg {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23000' stroke-dasharray='8, 10' stroke-dashoffset='7' stroke-linecap='square'/%3E%3C/svg%3E");
    transition: background-color 0.2s ease;
}

.theme-light .dashed-bg.drag-over {
    background-color: rgba(255, 255, 255, 0.2);
}

.theme-dark .dashed-bg.drag-over {
    background-color: rgba(0, 0, 0, 0.1);
}
</style>
